import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HelpCircle, Globe } from "lucide-react";

export default function FAQ() {
  const { t, i18n } = useTranslation(['pages.faq']);

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
  };

  const faqCategories = [
    {
      key: 'gettingStarted',
      title: t('pages.faq:categories.gettingStarted.title'),
      questions: [
        'gettingStarted.account',
        'gettingStarted.project',
        'gettingStarted.team',
        'gettingStarted.permissions'
      ]
    },
    {
      key: 'projects',
      title: t('pages.faq:categories.projects.title'),
      questions: [
        'projects.create',
        'projects.limit',
        'projects.members',
        'projects.plans'
      ]
    },
    {
      key: 'tickets',
      title: t('pages.faq:categories.tickets.title'),
      questions: [
        'tickets.create',
        'tickets.assign',
        'tickets.status',
        'tickets.ai'
      ]
    },
    {
      key: 'subscription',
      title: t('pages.faq:categories.subscription.title'),
      questions: [
        'subscription.plans',
        'subscription.upgrade',
        'subscription.cancel',
        'subscription.billing'
      ]
    },
    {
      key: 'technical',
      title: t('pages.faq:categories.technical.title'),
      questions: [
        'technical.browser',
        'technical.mobile',
        'technical.data',
        'technical.support'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 relative">
      {/* Language Selector */}
      <div className="absolute top-4 right-4 z-10">
        <Select value={i18n.language} onValueChange={handleLanguageChange}>
          <SelectTrigger className="w-32 bg-white/90 backdrop-blur-sm">
            <Globe className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="en">EN</SelectItem>
            <SelectItem value="de">DE</SelectItem>
            <SelectItem value="tr">TR</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <HelpCircle className="h-8 w-8 text-blue-600" />
              <CardTitle className="text-3xl font-bold">{t('pages.faq:title')}</CardTitle>
            </div>
            <p className="text-sm text-gray-600 mt-2">{t('pages.faq:subtitle')}</p>
          </CardHeader>
          <CardContent className="space-y-8">
            {faqCategories.map((category) => (
              <div key={category.key} className="space-y-4">
                <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">
                  {category.title}
                </h2>
                <Accordion type="single" collapsible className="w-full">
                  {category.questions.map((questionKey) => (
                    <AccordionItem key={questionKey} value={questionKey}>
                      <AccordionTrigger className="text-left">
                        {t(`pages.faq:questions.${questionKey}.question`)}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-700 leading-relaxed">
                        {t(`pages.faq:questions.${questionKey}.answer`)}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
            
            <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>{t('pages.faq:stillHaveQuestions.title')}</strong>
              </p>
              <p className="text-sm text-gray-600 mt-2">
                {t('pages.faq:stillHaveQuestions.content')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
